import type { CSSProperties, PropsWithChildren } from 'react';
import { useRevealOnScroll } from '../hooks/useRevealOnScroll';
import { cn } from '../utils';

type RevealProps = PropsWithChildren<{
  className?: string;
  delay?: number;
  threshold?: number;
  variant?: 'up' | 'left' | 'right';
  durationMs?: number;
}>;

export function Reveal({
  children,
  className,
  delay = 0,
  threshold,
  variant = 'up',
  durationMs = 560,
}: RevealProps) {
  const { ref, visible } = useRevealOnScroll<HTMLDivElement>({ threshold });

  const style: CSSProperties = {
    transitionDelay: `${delay}ms`,
    transitionDuration: `${durationMs}ms`,
    transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
  };

  const hiddenClass =
    variant === 'left'
      ? '-translate-x-4 opacity-0 sm:-translate-x-8'
      : variant === 'right'
      ? 'translate-x-4 opacity-0 sm:translate-x-8'
      : 'translate-y-4 opacity-0 sm:translate-y-7';

  return (
    <div
      ref={ref}
      style={style}
      className={cn(
        'transition-[transform,opacity] will-change-transform will-change-opacity motion-reduce:translate-x-0 motion-reduce:translate-y-0 motion-reduce:opacity-100 motion-reduce:transition-none',
        visible ? 'translate-x-0 translate-y-0 opacity-100' : hiddenClass,
        className
      )}
    >
      {children}
    </div>
  );
}
